import { useState, useCallback } from 'react';
import { reconciliationApi } from '../services/api/endpoints/reconciliation';
import { roundMoney } from '../utils/currency';
import type { 
  ReconcileResult, 
  ReconciliationLine, 
  ReconciliationGroups,
  UnreconciledParams 
} from '../types/reconciliation';

/**
 * Hook pour le lettrage comptable
 * Gère le cycle complet : chargement des lignes non lettrées,
 * lettrage, dé-lettrage, et consultation des groupes lettrés
 */
export function useReconciliation() {
  const [unreconciledLines, setUnreconciledLines] = useState<ReconciliationLine[]>([]);
  const [unreconciledTotal, setUnreconciledTotal] = useState(0);
  const [groups, setGroups] = useState<ReconciliationGroups>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Charger les lignes non lettrées d'un compte */
  const loadUnreconciled = useCallback(async (
    accountId: string, 
    params?: UnreconciledParams
  ) => {
    try {
      setLoading(true);
      setError(null);
      const response = await reconciliationApi.getUnreconciled(accountId, params);
      const payload = (response as any)?.data?.data ?? (response as any)?.data ?? response;
      const lines = Array.isArray(payload?.data) ? payload.data : [];
      setUnreconciledLines(lines);
      setUnreconciledTotal(payload?.total ?? lines.length);
    } catch (err: any) {
      const message = err?.message || err?.error || 'Erreur lors du chargement des lignes non lettrées';
      setError(message);
      setUnreconciledLines([]);
      setUnreconciledTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  /** Charger les groupes lettrés d'un compte */
  const loadGroups = useCallback(async (accountId: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await reconciliationApi.getGroups(accountId);
      const payload = (response as any)?.data?.data ?? (response as any)?.data ?? response;
      // Le backend renvoie un objet { "A": [...], "B": [...] }
      const groupsData = (payload && typeof payload === 'object' && !Array.isArray(payload)) 
        ? payload as ReconciliationGroups 
        : {};
      setGroups(groupsData);
    } catch (err: any) {
      const message = err?.message || err?.error || 'Erreur lors du chargement des groupes lettrés';
      setError(message);
      setGroups({});
    } finally {
      setLoading(false);
    }
  }, []);

  /** Lettrer des lignes sélectionnées */
  const reconcile = useCallback(async (
    lineIds: string[], 
    allowPartial?: boolean
  ): Promise<ReconcileResult | null> => {
    try {
      setLoading(true);
      setError(null);
      const response = await reconciliationApi.reconcile({ lineIds, allowPartial });
      const result = (response as any)?.data?.data ?? (response as any)?.data ?? response;
      return result as ReconcileResult;
    } catch (err: any) {
      const message = err?.message || err?.error || 'Erreur lors du lettrage';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Dé-lettrer un groupe */
  const unreconcile = useCallback(async (matchingNumber: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      await reconciliationApi.unreconcile(matchingNumber);
      return true;
    } catch (err: any) {
      const message = err?.message || err?.error || 'Erreur lors du dé-lettrage';
      setError(message);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Calculer la balance d'un ensemble de lignes */
  const computeBalance = useCallback((lines: ReconciliationLine[]): number => {
    return roundMoney(lines.reduce((sum, l) => sum + (Number(l.debit) || 0) - (Number(l.credit) || 0), 0));
  }, []);

  return {
    unreconciledLines,
    unreconciledTotal,
    groups,
    loading,
    error,
    loadUnreconciled,
    loadGroups,
    reconcile,
    unreconcile,
    computeBalance,
  };
}
